import { useNavigate } from 'react-router-dom';
import { sectionPath } from '../siteRoutes';
import React, { useState, useEffect } from 'react';
import { ChevronDown, MapPin, Star } from 'lucide-react';
import WallOfFameStrip from './WallOfFameStrip';
import { TrustBadge } from './Trust';
import { useHotel } from '../context/HotelContext';
import { useLanguage } from '../context/LanguageContext';
import { getTranslation } from '../i18n';

const Hero = () => {
  const navigate = useNavigate();
  const { hotelInfo, gallery } = useHotel();
  const { lang } = useLanguage();
  const t = (key) => getTranslation(lang, key);
  const [currentSlide, setCurrentSlide] = useState(0);

  // Use first 4 gallery images for hero slider
  const heroImages = gallery.slice(0, 4).map(img => img.url);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [heroImages.length]);

  const scrollToBooking = () => {
    const element = document.getElementById('rooms');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Pick the localised description & tagline: prefer per-language DB field, fall back to i18n bundle.
  const dbDescriptions = hotelInfo.description_translations || {};
  const dbDescription = dbDescriptions[lang] || dbDescriptions.en || '';
  const heroDescription = dbDescription || t('hero.description');

  const dbTaglines = hotelInfo.tagline_translations || {};
  const dbTagline = dbTaglines[lang] || dbTaglines.en || hotelInfo[`tagline_${lang}`] || '';
  const heroTagline = dbTagline || hotelInfo.tagline || t('hero.tagline');

  return (
    <section id="home" className="relative h-screen mt-[120px] lg:mt-[100px]">
      {/* Image Carousel */}
      <div className="absolute inset-0 overflow-hidden">
        {heroImages.map((img, index) => (
          <div
            key={img || index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={img}
              alt={index === 0 ? 'Hotel La Rosa Negra — beachfront hotel on Velika Plaža, Ulcinj' : `Hotel La Rosa Negra ${index + 1}`}
              className="w-full h-full object-cover"
              loading={index === 0 ? 'eager' : 'lazy'}
              fetchPriority={index === 0 ? 'high' : 'auto'}
              decoding="async"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
          </div>
        ))}
      </div>

      {/* Content */}
      <div className="relative h-full container mx-auto px-4 flex items-center">
        <div className="max-w-7xl mx-auto w-full grid lg:grid-cols-2 gap-8 items-center">
          {/* Left content */}
          <div className="text-white space-y-6">
            <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm w-fit px-4 py-2 rounded-full">
              <MapPin size={18} />
              <span className="text-sm font-medium">{hotelInfo.city}, {hotelInfo.country}</span>
            </div>

            <TrustBadge />

            <WallOfFameStrip />

            <h1 className="text-5xl lg:text-6xl font-bold leading-tight">
              {hotelInfo.name}
            </h1>

            <p className="text-xl lg:text-2xl text-gray-200 font-light">
              {heroTagline}
            </p>

            <p className="text-lg text-gray-300 max-w-xl">
              {heroDescription}
            </p>

            <div className="flex gap-4 pt-4">
              <button
                onClick={scrollToBooking}
                className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 py-4 rounded-full font-bold text-lg hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
                data-testid="hero-explore-rooms"
              >
                {t('hero.exploreRooms')}
              </button>
              <button
                onClick={() => { const el = document.getElementById('gallery'); if (el) el.scrollIntoView({ behavior: 'smooth' }); else navigate(sectionPath('gallery', window.location.pathname)); }}
                className="bg-white/20 backdrop-blur-sm text-white border-2 border-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white/30 transition-all"
                data-testid="hero-view-gallery"
              >
                {t('hero.viewGallery')}
              </button>
            </div>
          </div>

          {/* Right side - Hotel Info */}
          <div className="hidden lg:block bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
            <div className="text-white space-y-4">
              <div className="flex items-center gap-2">
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} size={20} className={star <= hotelInfo.stars ? "text-yellow-400 fill-yellow-400" : "text-white/30"} />
                  ))}
                </div>
                <span className="text-sm">{t('hero.starHotel')}</span>
              </div>
              <h3 className="text-2xl font-bold">{t('hero.bookDirect')}</h3>
              <ul className="space-y-2 text-gray-200">
                <li className="flex items-center gap-2">✓ {t('hero.bestPrice')}</li>
                <li className="flex items-center gap-2">✓ {t('hero.freeCancellation')}</li>
                <li className="flex items-center gap-2">✓ {t('hero.noBookingFees')}</li>
              </ul>
              <button
                onClick={scrollToBooking}
                className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-4 rounded-xl font-bold text-lg hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg mt-4"
                data-testid="hero-check-availability"
              >
                {t('hero.checkAvailability')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <ChevronDown size={32} className="text-white" />
      </div>

      {/* Slide indicators */}
      <div className="absolute bottom-8 right-8 flex gap-2">
        {heroImages.map((img, index) => (
          <button
            key={`dot-${img || index}`}
            onClick={() => setCurrentSlide(index)}
            className={`w-2 h-2 rounded-full transition-all ${
              index === currentSlide ? 'bg-orange-500 w-8' : 'bg-white/50'
            }`}
          />
        ))}
      </div>
    </section>
  );
};

export default Hero;
